import { Country } from 'constants/country'
import { Language } from 'translations/Language'
import { Nullable } from 'models/helpers'
import { OrganizationLight } from 'models/organization'
import { PreferredPaymentMethodType } from './preferredPaymentMethod'
import { WorkspaceUserProfile } from './userWorkspaceDTOs'

/** Types of clients */
export enum ClientType {
  B2B = 'B2B',
  B2C = 'B2C',
}

/** User's salutation */
export enum Salutation {
  MR = 'MR',
  MS = 'MS',
}

/** Platform-side role of a user (CLIENT / CREATIVE / AGENT). */
export enum PlatformRole {
  CLIENT = 'CLIENT',
  CREATIVE = 'CREATIVE',
  AGENT = 'AGENT',
}

/** Platform user interface */
export type PlatformUser = PlatformUserBase | PlatformUserClient | PlatformUserCreative

/** Platform user interface for client and admin */
export interface PlatformUserClient extends PlatformUserBase, PlatformUserCreative {
  preferredPaymentMethod: PreferredPaymentMethodType,
  stripeCustomerId?: string
  billingEmail?: string
  pipedriveId?: number
  clientType: ClientType
  workspaces: WorkspaceUserProfile[]
  /**
   * The workspace to preselect for this user, set from the one they most recently switched into, so a new
   * browser or device restores that choice instead of falling back to the first one. Absent until they
   * explicitly switch, and not guaranteed to still be accessible — always resolve it against `workspaces`
   * before selecting it.
   */
  preferredWorkspaceId?: string
  // B2B client fields
  organizationId?: string
  organizationSfId?: string
  personalOrderReference?: string
  userGroupIdentifier?: string
  // B2C client fields
  vatNumber?: string
  address?: string
  /** First time the user logged in */
  firstLoginDateTime: string
  ccEmails: string[]
}

/** Interface describing creative user of the platform  */
export interface PlatformUserCreative extends PlatformUserBase {
  enabled: boolean
  organizationId?: string
  organizationSfId?: string
}

/** Platform user base interface */
export interface PlatformUserBase {
  id?: string
  /** The user's main Auth0 identity, the same value as `id` (which the backend deprecates for it). */
  auth0Id?: string
  /** Platform UUID — the DB identifier. Backend field name is `internalId` (matches `UserProfileBaseDTO`).
   * Use this when calling endpoints that take UUIDs (e.g. `POST /conversations.participantIds`). */
  internalId?: string
  publicId?: string
  name?: string
  email?: string
  phone?: string
  country?: string
  defaultTimezone?: string
  language?: string
  sfRecordId?: string
  /** Platform-side role (CLIENT / CREATIVE / AGENT). Distinct from the Auth0 role surfaced by
   * `useAuth0().roles` — in BKBN dev, `@bkbn.com` signups get an ADMIN Auth0 role regardless of
   * their actual platform role. Use this field when matching against `ConversationParticipantRole`. */
  platformRole?: PlatformRole
}

/** Invoicing address details for user registration */
export interface InvoicingAddressDetailsDTO {
  addressName: string
  companyOrIndividualName: string
  streetAndNumber: string
  apartmentNumber?: string
  region?: string
  postalCode: string
  city: string
  country: Country
  vatNumber?: string
}

/** Create platform user API call body interface */
export interface CreatePlatformUserBody {
  name: string
  firstName: string
  lastName: string
  phone: string
  mobilePhone: string
  country: Country
  locale: {
    language: Language
    timezone: string
  }
  hasOrganization: boolean
  isOrganizationCorrect: boolean
  organization: Nullable<OrganizationLight>
  invoicingAddress?: InvoicingAddressDetailsDTO
  onboardingAnswer?: string
}

/** Platform user status interface */
export interface PlatformUserStatus {
  hasAuth0Account: boolean
  hasPipedriveContactPerson: boolean
  hasDatabaseProfile: boolean
  isEmailVerified: boolean
}

/** Update platform user body API call body interface */
export type UpdatePlatformUserBody = UpdatePlatformUserBaseBody | UpdatePlatformUserClientBody

/** Update platform user client API call body interface (extends platform user base) */
export interface UpdatePlatformUserClientBody extends UpdatePlatformUserBaseBody {
  preferredPaymentMethod: PreferredPaymentMethodType
  // B2B client fields
  personalOrderReference: string | null
  team: string | null
  // B2C client fields
  address: string | null
  vatNumber: string | null
}

/** Update platform user base API call body interface */
export interface UpdatePlatformUserBaseBody {
  name: string
  phone: string
  defaultTimezone: string
  language: Language
}
